import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';

const DeleteUserModal = ({ isOpen, onClose, onConfirm, user, loading = false }) => {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen || !user) return null;

  const expectedText = 'DELETE';
  const canDelete = confirmText === expectedText;

  const handleConfirm = () => {
    if (canDelete) {
      onConfirm(user.id);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('users.delete_user')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-600">
            <p className="mb-3">
              {t('users.delete_confirmation_message', {
                name: `${user.firstName} ${user.lastName}`,
                email: user.email
              })}
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-800 text-sm font-medium mb-2">
                {t('users.delete_warning_title')}
              </p>
              <ul className="text-red-700 text-sm space-y-1 list-disc list-inside">
                <li>{t('users.delete_warning_1')}</li>
                <li>{t('users.delete_warning_2')}</li>
                <li>{t('users.delete_warning_3')}</li>
              </ul>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('users.delete_confirmation_instruction', { text: expectedText })}
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={expectedText}
                className={`input ${confirmText && !canDelete ? 'border-red-500' : ''}`}
                disabled={loading}
                autoComplete="off"
              />
              {confirmText && !canDelete && (
                <p className="text-red-500 text-sm">
                  {t('users.delete_confirmation_mismatch')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t">
          <button
            onClick={handleClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            className="btn btn-danger"
            disabled={!canDelete || loading}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <LoadingSpinner size="sm" />
                <span>{t('users.deleting')}</span>
              </div>
            ) : (
              t('users.delete_user')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserModal;